/**
 * Перевод карточки контура канона (`/contours/{id}`) и его работ
 * (`/verification/queue?contour_id=`) в строки экрана Astra.
 *
 * Чистые функции: без React и без запросов — их проверяет тест, а экран
 * только рисует. Главное правило перевода — отсутствие ≠ ноль: площадь,
 * которой источник не назвал, остаётся `null` и печатается словами.
 */

import type { ContourDetail, VerificationQueue } from '../../../api/client';
import { toHa } from './dataset';
import { defineMessages, type Message } from '../../../i18n/define';
import { tr } from '../../../i18n/runtime';
import { cropName } from '../../../lib/crops';

const m = defineMessages({
  actorNo: {
    id: 'data.contour.actorNo',
    defaultMessage: 'участник №{id}',
    description: 'Имя землепользователя или хозяйства в карточке контура, когда сервер имя не назвал; {id} — номер участника',
  },
  noLandUser: {
    id: 'data.contour.noLandUser',
    defaultMessage: 'землепользователь не указан',
    description: 'Заголовок блока «Землепользователь» в карточке контура: в реестре пользователь не назван',
  },
  severalLandUsers: {
    id: 'data.contour.severalLandUsers',
    defaultMessage: 'несколько ({count})',
    description: 'Заголовок блока «Землепользователь»: у контура несколько пользователей; {count} — их число',
  },
  workNoReport: {
    id: 'data.contour.work.noReport',
    defaultMessage: 'без отчёта',
    description: 'Состояние работы на контуре: отчёта нет; коротко, строчная подпись в строке',
  },
  workPartial: {
    id: 'data.contour.work.partial',
    defaultMessage: 'отчёт частичный',
    description: 'Состояние работы на контуре: отчёт внесён по части площади; коротко',
  },
  workReported: {
    id: 'data.contour.work.reported',
    defaultMessage: 'отчитались',
    description: 'Состояние работы на контуре: хозяйство внесло отчёт; коротко',
  },
  workConfirmed: {
    id: 'data.contour.work.confirmed',
    defaultMessage: 'отмечено проверяющим',
    description: 'Состояние работы на контуре: проверяющий отметил отчёт; коротко',
  },
  workNeedsClarification: {
    id: 'data.contour.work.needsClarification',
    defaultMessage: 'нужно уточнение',
    description: 'Состояние работы на контуре: проверяющему нужно уточнение отчёта; коротко',
  },
  workConflict: {
    id: 'data.contour.work.conflict',
    defaultMessage: 'опровергнуто проверяющим',
    description: 'Состояние работы на контуре: проверяющий установил, что отчёт не соответствует действительности; коротко',
  },
  qGreen: {
    id: 'data.contour.quarantine.status.green',
    defaultMessage: 'зелёный',
    description: 'Цвет карантинного статуса контура словами (строчная фраза): зелёный',
  },
  qYellow: {
    id: 'data.contour.quarantine.status.yellow',
    defaultMessage: 'жёлтый',
    description: 'Цвет карантинного статуса контура словами (строчная фраза): жёлтый',
  },
  qRed: {
    id: 'data.contour.quarantine.status.red',
    defaultMessage: 'красный',
    description: 'Цвет карантинного статуса контура словами (строчная фраза): красный',
  },
  dirPests: {
    id: 'data.contour.quarantine.direction.pests',
    defaultMessage: 'вредители',
    description: 'Направление карантинного обследования: вредители (строчная подпись находки)',
  },
  dirDiseases: {
    id: 'data.contour.quarantine.direction.diseases',
    defaultMessage: 'болезни',
    description: 'Направление карантинного обследования: болезни (строчная подпись находки)',
  },
  dirWeeds: {
    id: 'data.contour.quarantine.direction.weeds',
    defaultMessage: 'сорняки',
    description: 'Направление карантинного обследования: сорняки (строчная подпись находки)',
  },
  scaleSpot: {
    id: 'data.contour.quarantine.scale.spot',
    defaultMessage: 'очаг',
    description: 'Масштаб находки при обследовании: очаг (небольшое пятно)',
  },
  scalePartial: {
    id: 'data.contour.quarantine.scale.partial',
    defaultMessage: 'часть контура',
    description: 'Масштаб находки при обследовании: затронута часть контура',
  },
  scaleWidespread: {
    id: 'data.contour.quarantine.scale.widespread',
    defaultMessage: 'весь контур',
    description: 'Масштаб находки при обследовании: затронут весь контур',
  },
  headlineDefaultGreen: {
    id: 'data.contour.quarantine.headline.defaultGreen',
    defaultMessage: 'Зелёный — по умолчанию: карантинных событий не зафиксировано. Это оговорка о данных, а не результат проверки.',
    description: 'Заголовок блока «Карантин» в карточке контура: зелёный статус стоит по умолчанию, обследований не было',
  },
  headlineSince: {
    id: 'data.contour.quarantine.headline.since',
    defaultMessage: 'Статус — {status}, с {date}.',
    description: 'Заголовок блока «Карантин»: статус и дата установления; {status} — цвет словами («красный»), {date} — дата',
  },
  headlineNoDate: {
    id: 'data.contour.quarantine.headline.noDate',
    defaultMessage: 'Статус — {status}; дата установления не названа.',
    description: 'Заголовок блока «Карантин»: статус известен, дата не названа; {status} — цвет словами',
  },
  problemNone: {
    id: 'data.contour.quarantine.problemNone',
    defaultMessage: 'проблема не названа',
    description: 'В строке находки обследования: название проблемы сервер не назвал (строчная фраза)',
  },
  findingScale: {
    id: 'data.contour.quarantine.findingScale',
    defaultMessage: '{what} — {scale}',
    description: 'Находка обследования с масштабом: {what} — «направление: проблема», {scale} — масштаб словами («очаг»)',
  },
  inspectionFound: {
    id: 'data.contour.quarantine.inspectionFound',
    defaultMessage: 'обследование: {found}',
    description: 'Строка истории обследований контура; {found} — находки через «; »',
  },
  inspectionFoundPart: {
    id: 'data.contour.quarantine.inspectionFoundPart',
    defaultMessage: 'обследование (часть {part}): {found}',
    description: 'Строка истории обследований контура с частью контура; {part} — название части, {found} — находки',
  },
  inspectionClean: {
    id: 'data.contour.quarantine.inspectionClean',
    defaultMessage: 'обследование: без находок',
    description: 'Строка истории обследований контура: обследование прошло, находок нет',
  },
  inspectionCleanPart: {
    id: 'data.contour.quarantine.inspectionCleanPart',
    defaultMessage: 'обследование (часть {part}): без находок',
    description: 'Строка истории обследований контура с частью контура: находок нет; {part} — название части',
  },
  cropNone: {
    id: 'data.contour.cropNone',
    defaultMessage: 'культура не названа',
    description: 'Название культуры озимого клина контура, когда сервер культуру не назвал (строчная фраза)',
  },
});


/* ------------------------------------------------------- землепользователи */

export interface ContourLandUser {
  /** Идентификатор субъекта канона — он же адрес `/farms/{id}`. */
  id: string;
  name: string;
  /** Подписи частей контура, на которых этот землепользователь стоит. */
  parts: string[];
}

export interface ContourLandUsers {
  /** Сколько разных землепользователей у контура сейчас. */
  count: number;
  users: ContourLandUser[];
  /**
   * Имя для строки «Хозяйство»: печатается ТОЛЬКО при единственном
   * землепользователе. При нескольких первое имя приписало бы общую землю
   * одному хозяйству — вместо него «несколько (N)».
   */
  headline: string;
}

/**
 * Землепользователи контура — действующие землепользования, по одному на
 * субъекта. Если действующих записей канон не прислал, берутся субъекты,
 * названные у частей контура: это тот же факт, записанный на уровне части.
 */
export function contourLandUsers(detail: ContourDetail): ContourLandUsers {
  const byId = new Map<number, ContourLandUser>();
  const add = (actor: { id: number; name: string | null }, part: string | null) => {
    const row = byId.get(actor.id) ?? {
      id: String(actor.id),
      name: actor.name ?? tr(m.actorNo, { id: actor.id }),
      parts: [],
    };
    if (part && !row.parts.includes(part)) row.parts.push(part);
    byId.set(actor.id, row);
  };

  for (const use of detail.land_uses) {
    if (use.is_current === false) continue;
    add(use.actor, use.part_label ?? null);
  }
  if (byId.size === 0) {
    for (const part of detail.parts) {
      for (const user of part.land_users) add(user, part.label ?? null);
    }
  }

  const users = [...byId.values()];
  const count = users.length;
  const headline =
    count === 0
      ? tr(m.noLandUser)
      : count === 1
        ? users[0]!.name
        : tr(m.severalLandUsers, { count });
  return { count, users, headline };
}

/* ------------------------------------------------------------------ площадь */

export interface ContourAreaRow {
  code: string;
  name: string;
  /** `null` — источник величину не назвал; печатается «нет данных». */
  ha: number | null;
  /** Строк без величины из всех строк категории. */
  missing: number;
  rows: number;
}

/** Код общей площади контура в итогах канона. */
export const TOTAL_LAND = 'TOTAL_LAND';

/**
 * Итоги по категориям земли. Категория, где у всех строк нет величины, —
 * `ha: null`, а не 0: канон сумму по ней не утверждал.
 */
export function contourAreaRows(detail: ContourDetail): ContourAreaRow[] {
  return detail.totals.map((total) => ({
    code: total.code,
    name: total.name,
    ha: total.rows > 0 && total.missing_rows >= total.rows ? null : toHa(total.area_microha),
    missing: total.missing_rows,
    rows: total.rows,
  }));
}

/** Общая площадь контура, га; `null` — источник её не назвал. */
export function contourTotalHa(detail: ContourDetail): number | null {
  return contourAreaRows(detail).find((row) => row.code === TOTAL_LAND)?.ha ?? null;
}

/* ------------------------------------------------------------------- работы */

/**
 * `alert` — отметка, которую нельзя пропустить глазом: проверяющий установил,
 * что факт не соответствует действительности. Обычная отметка проверяющего
 * нейтральна: отчёт и без неё принят как факт (журнал решений №28).
 */
export type WorkStateLabel = { text: string; quiet: boolean; alert?: boolean };

const WORK_STATE: Record<string, { text: Message; quiet: boolean; alert?: boolean }> = {
  no_data: { text: m.workNoReport, quiet: true },
  partial: { text: m.workPartial, quiet: false },
  reported: { text: m.workReported, quiet: false },
  confirmed: { text: m.workConfirmed, quiet: false },
  needs_clarification: { text: m.workNeedsClarification, quiet: true },
  conflict: { text: m.workConflict, quiet: false, alert: true },
};

export interface ContourWorkRow {
  key: string;
  workName: string;
  campaign: string;
  farmId: string;
  farmName: string;
  windowFrom: string | null;
  windowTo: string | null;
  state: WorkStateLabel;
  demo: boolean;
}

export function workStateLabel(state: string): WorkStateLabel {
  const known = WORK_STATE[state];
  if (!known) return { text: state, quiet: true };
  return { ...known, text: tr(known.text) };
}

/** Строки работ контура. Числа в них сужены сервером до самого контура. */
export function contourWorkRows(queue: VerificationQueue): ContourWorkRow[] {
  return queue.items.map((row) => ({
    key: String(row.farm_obligation_id),
    workName: row.work_name,
    campaign: row.campaign_title,
    farmId: String(row.actor.id),
    farmName: row.actor.name ?? tr(m.actorNo, { id: row.actor.id }),
    windowFrom: row.window_from,
    windowTo: row.window_to,
    state: workStateLabel(row.state),
    demo: row.demo_batch_id !== null || row.epistemic_status === 'synthetic_demo',
  }));
}

/* ----------------------------------------------------------------- карантин */

const Q_STATUS: Record<string, Message> = {
  green: m.qGreen,
  yellow: m.qYellow,
  red: m.qRed,
};

const Q_DIRECTION: Record<string, Message> = {
  pests: m.dirPests,
  diseases: m.dirDiseases,
  weeds: m.dirWeeds,
};

const Q_SCALE: Record<string, Message> = {
  spot: m.scaleSpot,
  partial: m.scalePartial,
  widespread: m.scaleWidespread,
};

/** Слово из словаря сообщений; незнакомый код показывается как есть. */
function word(dictionary: Record<string, Message>, code: string): string {
  const message = dictionary[code];
  return message ? tr(message) : code;
}

export interface QuarantineLine {
  key: string;
  date: string;
  text: string;
  demo: boolean;
}

export interface ContourQuarantineView {
  /** Фраза о статусе: для зелёного по умолчанию — с оговоркой. */
  headline: string;
  lines: QuarantineLine[];
  prevention: { total: number; reported: number };
}

/**
 * Карантин контура из блока карточки. Зелёный по умолчанию называется
 * «событий не было», а не «проверено» — так его определяет сам канон.
 */
export function contourQuarantine(detail: ContourDetail): ContourQuarantineView {
  const q = detail.quarantine;
  const status = word(Q_STATUS, q.status);
  const headline = q.is_default_green
    ? tr(m.headlineDefaultGreen)
    : q.effective_on
      ? tr(m.headlineSince, { status, date: q.effective_on })
      : tr(m.headlineNoDate, { status });

  const findingByInspection = new Map<number, string[]>();
  for (const f of q.findings) {
    const parts = [
      word(Q_DIRECTION, f.direction),
      f.problem_name ?? f.problem_code ?? tr(m.problemNone),
    ];
    const scale = f.scale ? word(Q_SCALE, f.scale) : null;
    const what = parts.join(': ');
    const text = scale ? tr(m.findingScale, { what, scale }) : what;
    findingByInspection.set(f.inspection_id, [...(findingByInspection.get(f.inspection_id) ?? []), text]);
  }

  const lines = q.inspections
    .map((insp) => {
      const found = findingByInspection.get(insp.id);
      const part = insp.part_label;
      const joined = found ? found.join('; ') : null;
      return {
        key: String(insp.id),
        date: insp.occurred_on,
        text: joined
          ? part
            ? tr(m.inspectionFoundPart, { part, found: joined })
            : tr(m.inspectionFound, { found: joined })
          : part
            ? tr(m.inspectionCleanPart, { part })
            : tr(m.inspectionClean),
        demo: insp.epistemic_status === 'synthetic_demo',
      };
    })
    .sort((a, b) => b.date.localeCompare(a.date));

  return {
    headline,
    lines,
    prevention: { total: q.prevention_obligations, reported: q.prevention_reported },
  };
}

/* ----------------------------------------------------------------- культуры */

export interface ContourCropView {
  /** Подпись сезона/года для KPI; null — источник сезона не назвал. */
  label: string | null;
  /** Имена культур через запятую; null — размещения нет. */
  value: string | null;
  demo: boolean;
}

interface CropSourceLike {
  placement_details: ReadonlyArray<{
    season: string;
    is_demo: boolean;
    by_crop: ReadonlyArray<{ crop_code: string; crop_name: string; area_microha: number }>;
  }>;
  crop_timeline: ReadonlyArray<{
    is_demo: boolean;
    rows: ReadonlyArray<{ crop_name: string | null; crop_code: string | null; phase: string; planned_from: string }>;
  }>;
}

/**
 * Культура текущего сезона контура — последний по сезону блок размещений
 * карточки. Блока нет — `value: null`: размещения канон по контуру не
 * назвал, и это не «пар».
 */
export function contourSeasonCrop(detail: CropSourceLike): ContourCropView {
  const block = [...detail.placement_details].sort((a, b) => b.season.localeCompare(a.season))[0];
  if (!block || block.by_crop.length === 0) {
    return { label: block?.season ?? null, value: null, demo: false };
  }
  const names = [...block.by_crop]
    .sort((a, b) => b.area_microha - a.area_microha)
    // Имя культуры — словарь интерфейса (№1652), а не сырое имя справочника
    // NGIS («Ғалла», «Пахта»): сырое — только для кода, которого словарь не знает.
    .map((c) => cropName(c.crop_code, c.crop_name));
  return { label: block.season, value: names.join(', '), demo: block.is_demo };
}

/**
 * Озимый клин контура — строки ротации с фазой `winter_grain`. Год подписи —
 * год сева (осень), как «озимая 2025» у прототипа: клин, посеянный осенью
 * перед сезоном `season`, а без сезона — последний по дате сева. Строки нет —
 * `value: null`: канон клина не назвал, и это не «озимой не было».
 */
export function contourWinterCrop(
  detail: CropSourceLike,
  season: string | null = null,
): ContourCropView {
  const sownYear = season && /^\d{4}$/.test(season) ? String(Number(season) - 1) : null;
  let best: { name: string; from: string; demo: boolean } | null = null;
  for (const block of detail.crop_timeline) {
    for (const row of block.rows) {
      if (row.phase !== 'winter_grain') continue;
      if (sownYear && !row.planned_from.startsWith(sownYear)) continue;
      if (!best || row.planned_from > best.from) {
        best = {
          name: row.crop_code || row.crop_name ? cropName(row.crop_code, row.crop_name) : tr(m.cropNone),
          from: row.planned_from,
          demo: block.is_demo,
        };
      }
    }
  }
  if (!best) return { label: sownYear, value: null, demo: false };
  return { label: best.from.slice(0, 4), value: best.name, demo: best.demo };
}
